import { useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { ImageHotspotsComponent } from '@apps/image-hotspots';
import { ElementToolbar } from '@pm2/element-toolbar';
import { scrollDirectionStore } from '@pm2/react-utils';
import { usePM2DisplayContext, pm2ConfigStore } from '@pm2/store';
import { usePm2Modal } from '@pm2/ui-lightbox';
import { componentFrameworkStore } from '@provider/component-framework';
import { UniversalLightboxCard, UniversalHarmonicaCard } from '@utils/component-framework';
import { useToggleState, SizeTransition } from '@utils/react';
import { EmbedContent } from './comp.embed-integration.js';
import { PM2ImageHotspotsComponent } from './comp.root.js';
import { card, contentTransitionDurationMs, content } from './comp.router.style.js';
import { ScrambledCard } from './comp.scrambled.js';
import { jsx } from 'react/jsx-runtime';

function Router(props) {
  const modal = usePm2Modal();
  const {
    inLightbox
  } = usePM2DisplayContext();
  const resolvedDisplayMode = props.displayMode;
  const badgeImage = pm2ConfigStore.useSelector(state => state.config.theme.componentPreviewImage);
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const [expanded, expansionActions] = useToggleState();
  const state = (() => {
    if (props.isScrambled) {
      return "scrambled";
    }
    if (inLightbox) {
      return "standalone";
    }
    if (!expanded) {
      return resolvedDisplayMode;
    }
    return "embed";
  })();
  const onLightboxActivate = useCallback(() => {
    modal.fromComponentFrameworkNode(/*#__PURE__*/jsx(PM2ImageHotspotsComponent, {
      ...props
    }), "component-framework.geogebra");
  }, [modal, props]);
  const onActivate = useCallback(() => {
    if (resolvedDisplayMode === "lightbox") {
      onLightboxActivate();
      return;
    }
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.activateComponent(props.node.component.envelope.ID));
    expansionActions.enable();

    // Force the header to collapse upon opening the component
    scrollDirectionStore.dispatch(scrollDirectionStore.actions.forceDirection("down"));
  }, [expansionActions, onLightboxActivate, props.node.component.envelope.ID, resolvedDisplayMode]);
  const onDeactivate = useCallback(() => {
    expansionActions.disable();
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.deactivateComponent(props.node.component.envelope.ID));
  }, [expansionActions, props.node.component.envelope.ID]);
  const onCollapse = useCallback(() => {
    expansionActions.disable();
    onDeactivate();
  }, [expansionActions, onDeactivate]);
  const content$1 = (() => {
    switch (state) {
      case "embed":
        return /*#__PURE__*/jsx(EmbedContent, {
          ...props,
          onActivate: onActivate,
          onDeactivate: onDeactivate,
          onCollapse: resolvedDisplayMode === "harmonica" ? onCollapse : undefined
        });
      case "standalone":
        return /*#__PURE__*/jsx(ImageHotspotsComponent, {
          context: "lightbox",
          component: props.node.component,
          node: props.node
        });
      case "harmonica":
        return /*#__PURE__*/jsx(UniversalHarmonicaCard, {
          className: card,
          title: props.node.component.envelope.Title,
          description: props.richtextDescription,
          audience: audience,
          badgeImageSrc: badgeImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onActivate,
          children: /*#__PURE__*/jsx(UniversalHarmonicaCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "lightbox":
        return /*#__PURE__*/jsx(UniversalLightboxCard, {
          className: card,
          title: props.node.component.envelope.Title,
          description: props.lightboxDescription,
          audience: audience,
          badgeImageSrc: badgeImage,
          previewImageSrc: props.previewImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onLightboxActivate,
          children: /*#__PURE__*/jsx(UniversalLightboxCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "scrambled":
        return /*#__PURE__*/jsx(ScrambledCard, {
          className: card,
          title: props.node.component.envelope.Title
        });
    }
  })();
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: /*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      swappable: true,
      classNames: content,
      timeout: contentTransitionDurationMs,
      delayMs: {
        enter: 10,
        exit: 0
      },
      onEnter: () => document.documentElement.style.setProperty("overflow-anchor", "none"),
      onEntered: () => document.documentElement.style.setProperty("overflow-anchor", ""),
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        "data-node-id": props.node.component.envelope.ID,
        className: content,
        children: content$1
      })
    }, state)
  });
}

export { Router };
//# sourceMappingURL=comp.router.js.map
